import { api } from './api'
import { Payment, PaymentStatus, ApiResponse } from '../types'

export interface CreatePaymentData {
  caseId: string
  amount: number
  description: string
  paymentMethod: 'BANK_TRANSFER' | 'REDIPAY'
  reference?: string
}

export interface CreateRediPayPaymentData {
  caseId: string
  amount: number
  description: string
  referenceNo: string
  itemName: string
  callbackUrl: string
  userEmail: string
  userName: string
  userPhone?: string
}

export interface BankTransferInfo {
  bankAccount: {
    id: string
    accountNumber: string
    accountName: string
    bankName: string
    bankCode: string
    isPrimary: boolean
  }
  referenceNumber: string
  amount: number
  dueDate?: string
}

export interface BookingInfo {
  id: string
  status: string
  serviceType: string
  scheduledDateTime: string
  serviceProvider: {
    id: string
    companyName?: string
    user?: {
      firstName: string
      lastName: string
    }
  }
}

export interface PaymentInitiationInfo {
  caseInfo: {
    id: string
    caseNumber: string
    deceasedName: string
    estimatedTotal: number
  }
  existingPayment?: {
    id: string
    status: string
    amount: number
  }
  bankAccounts: {
    id: string
    bankName: string
    accountHolderName: string
    accountNumber: string
    accountNumberLast: string
    currency: string
    description: string
    displayPriority: number
    minimumAmount: number
  }[]
  paymentDueDate: string
  bookingInfo: {
    bookings: BookingInfo[]
    canInitiatePayment: boolean
    unapprovedBooking?: {
      status: string
      serviceType: string
    }
  }
}

export interface PaymentVerificationData {
  paymentId: string
  verificationStatus: 'PENDING' | 'VERIFIED' | 'REJECTED'
  verificationNotes?: string
  adminNotes?: string
  rejectionReason?: string
}

export interface UploadPaymentReceiptData {
  paymentId: string
  receiptFile: File
}

class PaymentService {
  async getPaymentsByCase(caseId: string): Promise<Payment[]> {
    const response = await api.get<ApiResponse<Payment[]>>(`/payments/case/${caseId}`)
    return response.data.data
  }

  async createPayment(data: CreatePaymentData): Promise<Payment> {
    const response = await api.post<Payment>('/payments/initiate', data)
    return response.data
  }

  async getPaymentInitiationInfo(caseId: string): Promise<PaymentInitiationInfo> {
    const response = await api.get<PaymentInitiationInfo>(`/payments/initiate/${caseId}`)
    return response.data
  }

  async getBankTransferInfo(caseId: string): Promise<BankTransferInfo> {
    const response = await api.get<BankTransferInfo>(`/payments/bank-transfers/case/${caseId}`)
    return response.data
  }

  async uploadPaymentReceipt(data: UploadPaymentReceiptData): Promise<Payment> {
    const formData = new FormData()
    formData.append('receiptFile', data.receiptFile)

    const response = await api.post<ApiResponse<Payment>>(
      `/payments/${data.paymentId}/receipt`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    )
    return response.data.data
  }

  async getPendingPayments(): Promise<Payment[]> {
    const response = await api.get<{ payments: Payment[], pagination: any }>('/payments/bank-transfers?status=PENDING')
    return response.data.payments
  }

  async getAllBankTransferPayments(): Promise<Payment[]> {
    const response = await api.get<{ payments: Payment[], pagination: any }>('/payments/bank-transfers')
    return response.data.payments
  }

  async verifyPayment(data: PaymentVerificationData): Promise<Payment> {
    const requestBody: any = {
      verificationStatus: data.verificationStatus
    }

    // Only include optional fields if they have values
    if (data.verificationNotes && data.verificationNotes.trim()) {
      requestBody.verificationNotes = data.verificationNotes.trim()
    }

    if (data.adminNotes && data.adminNotes.trim()) {
      requestBody.adminNotes = data.adminNotes.trim()
    }

    if (data.rejectionReason && data.rejectionReason.trim()) {
      requestBody.rejectionReason = data.rejectionReason.trim()
    }

    const response = await api.patch<ApiResponse<Payment>>(
      `/payments/bank-transfers/${data.paymentId}/verify`,
      requestBody
    )
    return response.data.data
  }

  async getPaymentById(paymentId: string): Promise<Payment> {
    const response = await api.get<ApiResponse<Payment>>(`/payments/${paymentId}`)
    return response.data.data
  }

  async getBankAccountsWithFullDetails(accountIds?: string[]): Promise<any[]> {
    const params = accountIds ? { accountIds: accountIds.join(',') } : {}
    const response = await api.get<any[]>('/payments/bank-accounts/full-details', { params })
    return response.data
  }

  getPaymentStatusColor(status: PaymentStatus): string {
    switch (status) {
      case PaymentStatus.PENDING:
        return 'bg-yellow-100 text-yellow-800'
      case PaymentStatus.PARTIAL:
        return 'bg-blue-100 text-blue-800'
      case PaymentStatus.PAID:
        return 'bg-green-100 text-green-800'
      case PaymentStatus.REFUNDED:
        return 'bg-red-100 text-red-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  getPaymentStatusIcon(status: PaymentStatus): string {
    switch (status) {
      case PaymentStatus.PENDING:
        return ''
      case PaymentStatus.PARTIAL:
        return ''
      case PaymentStatus.PAID:
        return ''
      case PaymentStatus.REFUNDED:
        return ''
      default:
        return ''
    }
  }

  formatPaymentStatus(status: PaymentStatus): string {
    return status.replace('_', ' ').toLowerCase().replace(/\b\w/g, l => l.toUpperCase())
  }

  getServiceName(serviceType: string): string {
    switch (serviceType) {
      case 'AMBULANCE_TRANSPORT': return 'Ambulance Transport'
      case 'GHUSL_SERVICE': return 'Ghusl Service'
      case 'KAFAN_SERVICE': return 'Kafan Service'
      case 'JANAZAH_LEAD': return 'Janazah Lead'
      case 'BURIAL_PLOT': return 'Burial Plot'
      case 'CATERING': return 'Catering'
      case 'FLOWERS': return 'Flowers'
      case 'TENT_RENTAL': return 'Tent Rental'
      case 'PHOTOGRAPHY': return 'Photography'
      case 'YASIN_BOOKS': return 'Yasin Books'
      case 'ISLAMIC_SOUVENIRS': return 'Islamic Souvenirs'
      default: return serviceType.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, l => l.toUpperCase())
    }
  }

  getProviderName(serviceProvider: any): string {
    // Handle null/undefined serviceProvider
    if (!serviceProvider) {
      return 'Service Provider';
    }

    // Try to get company name first
    if (serviceProvider.companyName) {
      return serviceProvider.companyName;
    }

    // Fall back to user's name
    if (serviceProvider.user) {
      const firstName = serviceProvider.user.firstName || '';
      const lastName = serviceProvider.user.lastName || '';
      const fullName = `${firstName} ${lastName}`.trim();
      if (fullName) {
        return fullName;
      }
    }

    // Final fallback
    return 'Service Provider';
  }

  // REDIPAY INTEGRATION: Create RediPay payment
  async createRediPayPayment(data: CreateRediPayPaymentData): Promise<any> {
    const response = await api.post('/payments/redipay', data);
    return response.data;
  }

  // REDIPAY INTEGRATION: Get payment status by RediPay ID
  async getRediPayPaymentStatus(paymentId: string): Promise<any> {
    const response = await api.get(`/redipay/payments/${paymentId}/status`);
    return response.data;
  }
}

export const paymentService = new PaymentService()